import React from 'react';
import { User, Eye, MessageCircle, Heart, Calendar } from 'lucide-react';

const ProfilePreview = ({ profileData }) => {
  // Generate avatar display - prioritize stored SVG, then URL, then initials
  const getAvatarDisplay = (sizeClass = 'h-20 w-20', textClass = 'text-2xl') => {
    // Use stored SVG content (from AvatarBuilder onUpdate callback)
    if (profileData?.avatar?.svgContent) {
      return (
        <div
          className={`${sizeClass} rounded-full overflow-hidden bg-primary/10 flex items-center justify-center shadow-lg`}
          dangerouslySetInnerHTML={{ __html: profileData?.avatar?.svgContent }}
        />
      );
    }

    // Use stored SVG from database (avatar.svg field)
    if (profileData?.avatar?.svg) {
      const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(profileData?.avatar?.svg)}`;
      return (
        <img
          src={dataUrl}
          alt="User avatar"
          className={`${sizeClass} rounded-full object-cover shadow-lg`}
        />
      );
    }

    // Use stored SVG URL
    if (profileData?.avatar?.svgUrl) {
      return (
        <img
          src={profileData?.avatar?.svgUrl}
          alt="User avatar"
          className={`${sizeClass} rounded-full object-cover shadow-lg`}
        />
      );
    }

    // Use DiceBear URL from config/seed
    if (profileData?.avatar?.config || profileData?.avatar?.seed) {
      const seed = profileData?.avatar?.seed || 'default';
      const dicebearUrl = `https://api.dicebear.com/7.x/open-peeps/svg?seed=${seed}`;
      return (
        <img
          src={dicebearUrl}
          alt="User avatar"
          className={`${sizeClass} rounded-full bg-primary/10 shadow-lg`}
        />
      );
    }

    // Fallback to initials
    const initials = ((profileData?.firstName?.[0] || '') + (profileData?.lastName?.[0] || ''))?.toUpperCase() || 'U';
    return (
      <div className={`${sizeClass} bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center shadow-lg`}>
        <span className={`${textClass} font-bold text-primary-foreground`}>{initials}</span>
      </div>
    );
  };

  const sampleReflection = {
    verse: "Philippians 4:13",
    text: "I can do all things through Christ who strengthens me.",
    reflection: "This verse has been my anchor during challenging times. It reminds me that with God's strength, no obstacle is too great.",
    likes: 12,
    comments: 3,
    timeAgo: "2 hours ago"
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
          <Eye className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground">Community Preview</h2>
          <p className="text-muted-foreground">How others see your profile</p>
        </div>
      </div>

      {/* Profile Header Preview */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-6 mb-6 border border-primary/20">
        <div className="flex items-center space-x-4 mb-4">
          {getAvatarDisplay('h-20 w-20', 'text-2xl')}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-foreground">
              {profileData?.firstName && profileData?.lastName
                ? `${profileData?.firstName} ${profileData?.lastName}`
                : profileData?.firstName || profileData?.lastName || 'User'}
            </h3>
            <p className="text-primary font-medium">
              @{profileData?.username || 'username'}
            </p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
              <span>🔥 {profileData?.stats?.studyStreak || 0} day streak</span>
              <span>📖 {profileData?.stats?.versesMemorized || 0} verses</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {profileData?.privacy?.showStats && (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center bg-card rounded-lg p-3 border border-border">
              <div className="text-xl font-bold text-primary">{profileData?.stats?.versesMemorized || 0}</div>
              <div className="text-xs text-muted-foreground">Verses</div>
            </div>
            <div className="text-center bg-card rounded-lg p-3 border border-border">
              <div className="text-xl font-bold text-warning">{profileData?.stats?.studyStreak || 0}</div>
              <div className="text-xs text-muted-foreground">Days</div>
            </div>
            <div className="text-center bg-card rounded-lg p-3 border border-border">
              <div className="text-xl font-bold text-success">{profileData?.stats?.communityContributions || 0}</div>
              <div className="text-xs text-muted-foreground">Posts</div>
            </div>
          </div>
        )}
      </div>

      {/* Sample Reflection Preview */}
      <div>
        <h4 className="font-semibold text-foreground mb-3">Recent Reflection</h4>

        {profileData?.privacy?.reflectionVisibility === 'private' ? (
          <div className="bg-muted/40 border-2 border-dashed border-border rounded-lg p-6 text-center">
            <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <User className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">Private Profile</p>
            <p className="text-sm text-muted-foreground/70">Reflections are set to private</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start space-x-3 mb-3">
              {getAvatarDisplay('h-10 w-10', 'text-sm')}
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <p className="font-medium text-foreground">@{profileData?.username || 'username'}</p>
                  <span className="text-muted-foreground">•</span>
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    <span>{sampleReflection?.timeAgo}</span>
                  </div>
                </div>
                <p className="text-sm text-primary font-medium mb-2">{sampleReflection?.verse}</p>
                <p className="text-sm text-muted-foreground italic mb-2">"{sampleReflection?.text}"</p>
                <p className="text-sm text-foreground">{sampleReflection?.reflection}</p>
              </div>
            </div>

            {profileData?.privacy?.communityInteractions && (
              <div className="flex items-center space-x-4 pt-3 border-t border-border">
                <button className="flex items-center space-x-2 text-muted-foreground hover:text-destructive transition-colors">
                  <Heart className="w-4 h-4" />
                  <span className="text-sm">{sampleReflection?.likes}</span>
                </button>
                <button className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm">{sampleReflection?.comments}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="h-6 w-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <Eye className="w-3 h-3 text-primary" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">Privacy Status</p>
            <div className="space-y-1 text-muted-foreground">
              <p>• Reflections: <span className="capitalize text-foreground">{profileData?.privacy?.reflectionVisibility || 'private'}</span></p>
              <p>• Community interactions: <span className="text-foreground">{profileData?.privacy?.communityInteractions ? 'Enabled' : 'Disabled'}</span></p>
              <p>• Statistics visibility: <span className="text-foreground">{profileData?.privacy?.showStats ? 'Public' : 'Hidden'}</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePreview;